import { useMutation } from "@tanstack/react-query"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import { marketingIngredientsMirrorColumns, routes } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"

/** What `POST /product-visuals/:store/:simpleSku/preview?kind=` answers with.
 *  Only the fields the gallery consumes are modeled: the fresh JPEG, the reason a
 *  fresh source re-resolve still cannot render this kind, and — since #349 — the
 *  fill trace, which is where the mirror columns the fill actually read are named.
 *  `content` is optional so an older or partial response is not a parse failure. */
type ProductVisualPreviewResponse = {
  targetGap: string | null
  kinds: ({ visualKind: ProductVisualKind } & (
    | {
        status: "ready"
        imageDataUrl: string | null
        content?: { filledFrom?: { trace?: string[] } }
      }
    | { status: "gap"; reason: string }
  ))[]
}

/** One cell's live preview. **Session-ephemeral by design** — the preview POST
 *  persists the canonical content it resolved but never an image, so nothing
 *  survives a reload. The UI labels it as such.
 *
 *  A union rather than two independently-nullable fields: a settled render either
 *  produced a JPEG or names why it could not, so the silent "no image and no
 *  reason" cell — which would render as an empty square with no explanation — is
 *  unrepresentable instead of merely unreached. */
export type ProductVisualRenderedPreview = {
  simpleSku: string
  visualKind: ProductVisualKind
  /** The catalog-mirror columns the re-fill read, off `filled_from.trace` (issue
   *  #349). Non-empty for `marketing-ingredients` only: it is the one filler that
   *  names its columns, because it runs on a stand-in the pixels cannot reveal. The
   *  grid kinds parse a PIM table, so they have no single column to name;
   *  `etiquette` DOES read three mirror columns but stamps
   *  only `magento_eav:selected_simple_first`, so it comes back empty too. Empty for
   *  a gap as well, which has no content row to attribute. */
  sourceColumns: string[]
} & ({ imageDataUrl: string; gapReason: null } | { imageDataUrl: null; gapReason: string })

export type ProductVisualRenderInput = { simpleSku: string; visualKind: ProductVisualKind }

/** Render one simple × kind from the product page. It re-resolves sources, so a
 *  stale `gap` cell can flip to an image — which is why the gallery offers it on
 *  every kind cell, not only the renderable ones. */
export function useRenderProductVisualPreview(store: string) {
  return useMutation<ProductVisualRenderedPreview, Error, ProductVisualRenderInput>({
    mutationFn: async ({ simpleSku, visualKind }) => {
      const result = await apiFetch<ProductVisualPreviewResponse>(
        routes.productVisuals.preview({ store, simpleSku }),
        { method: "POST", query: { kind: visualKind } }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      const rendered = result.value.kinds.find((kind) => kind.visualKind === visualKind)
      const imageDataUrl = rendered?.status === "ready" ? rendered.imageDataUrl : null
      const sourceColumns =
        rendered?.status === "ready"
          ? marketingIngredientsMirrorColumns(rendered.content?.filledFrom?.trace ?? [])
          : []
      if (imageDataUrl !== null) {
        return { simpleSku, visualKind, sourceColumns, imageDataUrl, gapReason: null }
      }
      const gapReason =
        result.value.targetGap ?? (rendered?.status === "gap" ? rendered.reason : null)
      return {
        simpleSku,
        visualKind,
        sourceColumns,
        imageDataUrl: null,
        // A `ready` kind that came back without a JPEG carries no reason of its
        // own. Unreachable via the current API, but the union forces a value, and
        // a named failure beats an unexplained empty square.
        gapReason: gapReason ?? "render_produced_no_image",
      }
    },
  })
}
